import { useCallback, useEffect, useMemo, useState } from "react";
import { axiosGateway } from "../api/axios";

const SAVED_URL = "/medical-cases/saved";

const keyOf = (type, id) => `${type}:${String(id ?? "").trim()}`;

const unwrap = (payload) => {
  const value = payload?.data ?? payload;
  return Array.isArray(value) ? value : value?.items || [];
};

export default function useSavedItems({ enabled = true } = {}) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(enabled ? "loading" : "idle");
  const [error, setError] = useState(null);
  const [pendingKey, setPendingKey] = useState(null);

  const reload = useCallback(async () => {
    if (!enabled) return [];
    setStatus("loading");
    setError(null);
    try {
      const response = await axiosGateway.get(SAVED_URL);
      const next = unwrap(response.data);
      setItems(next);
      setStatus("succeeded");
      return next;
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Failed to load saved items";
      setError(message);
      setStatus("failed");
      throw err;
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled) reload().catch(() => {});
  }, [enabled, reload]);

  const savedKeys = useMemo(
    () => new Set(items.map((item) => keyOf(item.targetType, item.targetId))),
    [items]
  );

  const isSaved = useCallback(
    (type, id) => savedKeys.has(keyOf(type, id)),
    [savedKeys]
  );

  const toggleSave = useCallback(
    async (targetType, targetId) => {
      const id = String(targetId ?? "").trim();
      if (!enabled || !id) return;
      const itemKey = keyOf(targetType, id);
      setPendingKey(itemKey);
      setError(null);
      try {
        if (savedKeys.has(itemKey)) {
          try {
            await axiosGateway.delete(`${SAVED_URL}/${encodeURIComponent(targetType)}/${encodeURIComponent(id)}`);
          } catch (err) {
            if (err?.response?.status !== 404) throw err;
          }
          setItems((current) => current.filter((item) => keyOf(item.targetType, item.targetId) !== itemKey));
        } else {
          const response = await axiosGateway.post(SAVED_URL, { targetType, targetId: id });
          const saved = response.data?.data ?? response.data;
          setItems((current) => [saved, ...current.filter((item) => keyOf(item.targetType, item.targetId) !== itemKey)]);
        }
      } catch (err) {
        setError(err?.response?.data?.message || err?.message || "Unable to update saved item");
        throw err;
      } finally {
        setPendingKey(null);
      }
    },
    [enabled, savedKeys]
  );

  return { items, status, error, reload, isSaved, toggleSave, pendingKey };
}
